<template lang="pug">
v-dialog(
  persistent
  width="290px"
  max-width="290px"
  v-model="isOpenTimePicker"
)
  template(v-slot:activator="{ on, attrs }")
    validation-observer(ref="timePickerObserver" v-slot="{ invalid }")
      validation-provider(:rules="{'required': required}" v-slot="{ errors }" :name="$t('common.time_picker')")
        v-text-field.time-picker__input(
          ref="timeInputRef"
          :class="{'input-time-outlined': outlined, 'input-time': !outlined}"
          :value="timeDisplay"
          :disabled="disable"
          :readonly="readonly"
          :hide-details="hideDetails"
          :outlined="outlined"
          :dense="dense"
          v-next-focus:[group]
          :style="maxWidth ? `max-width: ${maxWidth}px` : ''"
          :error-messages="getErrorMessages(errors)"
          color="green"
          :clearable="clearable"
          append-icon="mdi-clock-time-four-outline"
          @click:append="isOpenTimePicker = true"
          @click:clear="clearTime()"
          @keydown.tab="handleInputValue"
          @keyup.enter="handleInputValue"
          @blur="handleInputValue"
          @focus="focusInput"
        )
          template(#label v-if='!hideLabel')
            slot(name="label")
              span {{ label || $t('common.time_picker') }}
              span(v-if="required" ).red--text *
  v-time-picker.time-picker__ui(
    v-model='timeData'
    scrollable
    width="290"
    color="primary"
    format="ampm"
    header-color="primary"
    @click:hour='onClickHour'
    @change="handleChooseTimePicker"
  )
    v-spacer
    v-btn.white--text.mt-3(
      dark 
      :large="!$vuetify.breakpoint.xsOnly" 
      color="red" 
      text 
      @click="isOpenTimePicker = false")
        span {{$t('common.cancel')}}
</template>

<script>
import { defineComponent, ref, computed, nextTick, watch } from 'vue'
import { isEmpty, isNaN } from 'lodash'
import { ValidationObserver } from 'vee-validate'
import { convertToWidthCharacter, safeParseInt } from '@/utils'
import moment from 'moment'

export default defineComponent({
  components: {
    ValidationObserver
  },
  props: {
    value: { type: String, default: null },
    label: { type: String, default: null },
    required: { type: Boolean, default: false },
    group: { type: String, default: 'default' },
    outlined: { type: Boolean, default: true },
    dense: { type: Boolean, default: true },
    hideDetails: { type: Boolean, default: false },
    disable: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    maxWidth: { type: Number, default: null },
    hideLabel: { type: Boolean, default: false },
    hideSeconds: { type: Boolean, default: false },
    errorMessages: { type: Array, default: null },
    isEmitNullBeforeSave: { type: Boolean, default: true },
    allowInputSeconds: { type: Boolean, default: false }
  },
  setup(props, { emit }) {
    const timeData = ref(props.value)
    const timeDisplay = computed(() => {
      if (props.hideSeconds) {
        if (props.value) {
          const timeObj = moment(props.value, 'HH:mm')
          return timeObj.format('HH:mm')
        } else {
          return props.value
        }
      }
      return props.value
    })
    const timeInputRef = ref(null)
    const hourSelected = ref(null)
    const isOpenTimePicker = ref(false)

    const saveTime = (time) => {
      timeInputRef.value.blur()
      emit('save-time', time)
    }

    const clearTime = () => {
      saveTime(null)
      timeData.value = null
      timeInputRef.value.reset()
    }

    const formatTime = (str) => {
      switch (str.length) {
        case 1:
          return str.padEnd(3, '0').padStart(4, '0')
        case 2:
          return str.padEnd(4, '0')
        default:
          return str.padStart(4, '0').substring(0, 4)
      }
    }
    const formatTimeSeconds = (str) => {
      switch (str.length % 2) {
        case 1:
          // input 1 --> 01:00:00
          return str.padEnd(5, '0').padStart(6, '0')
        case 0:
          // input 11 --> 11:00:00
          return str.padEnd(6, '0')
        default:
          return str.padStart(6, '0').substring(0, 6)
      }
    }
    const getHoursAndMinutesAndSeconds = (value) => {
      const paddedTimeString = formatTimeSeconds(value)
      let hours = paddedTimeString.slice(0, 2)
      let minutes = paddedTimeString.slice(2, 4)
      let seconds = paddedTimeString.slice(-2)
      return { hours, minutes, seconds }
    }

    const getHoursAndMinutes = (value) => {
      const paddedTimeString = formatTime(value)
      let hours = paddedTimeString.slice(0, -2)
      let minutes = paddedTimeString.slice(-2)
      return { hours, minutes }
    }

    const handleInputValue = (event) => {
      const value = (event?.target?._value || '').replace(/:/g, '')
      if (isEmpty(value) || isNaN(safeParseInt(value))) {
        clearTime()
        return
      }
      if (props.isEmitNullBeforeSave) {
        emit('save-time', null) // reset value before update value
      }
      nextTick(() => {
        let result = null
        if (props.allowInputSeconds) {
          const { hours, minutes, seconds } =
            getHoursAndMinutesAndSeconds(value)
          if (
            parseInt(hours) > 23 ||
            parseInt(minutes) > 59 ||
            parseInt(seconds) > 59
          ) {
            clearTime()
            return
          }
          result = `${hours}:${minutes}:${seconds}`
        } else {
          const { hours, minutes } = getHoursAndMinutes(value)
          if (parseInt(hours) > 23 || parseInt(minutes) > 59) {
            clearTime()
            return
          }
          result = `${hours}:${minutes}`

          if (!props.hideSeconds) result = `${result}:00`
        }
        timeData.value = result
        saveTime(result)
      })
    }

    const onClickHour = (hour) => {
      if (timeData.value) {
        hourSelected.value = hour
      }
    }

    watch(
      () => isOpenTimePicker.value,
      (newVal) => {
        if (!newVal && hourSelected.value && timeData.value) {
          const minutes = timeData.value.split(':')[1]
          const { hours: hh, minutes: mm } = getHoursAndMinutes(
            `${hourSelected.value}${minutes}`
          )
          const result = `${hh}:${mm}`
          timeData.value = result
          hourSelected.value = null
          saveTime(result)
        }
      }
    )

    const focusInput = (e) => {
      setTimeout(() => {
        e.target.select()
      })
      emit('handleFocus', e.target)
    }

    const handleChooseTimePicker = (event) => {
      saveTime(event)
      isOpenTimePicker.value = false
    }

    const getErrorMessages = (errors) => {
      if (errors.length > 0) return errors
      return props.errorMessages
    }

    return {
      timeData,
      timeDisplay,
      timeInputRef,
      isOpenTimePicker,
      handleInputValue,
      handleChooseTimePicker,
      clearTime,
      focusInput,
      getErrorMessages,
      onClickHour
    }
  }
})
</script>

<style lang="sass" scoped>
.input-time-outlined
  ::v-deep
    .v-input__append-inner:has(.v-input__icon--append)
      position: absolute
      left: 5px
      top: 0px
    input
      margin-left: 30px !important
    .v-label
      margin-left: 30px !important
    .v-label--active
      transform: translate(-30px, -18px) scale(0.75) !important

.input-time
  ::v-deep
    .v-input__append-inner:has(.v-input__icon--append)
      position: absolute
      left: -5px
      top: 1px
    input
      margin-left: 30px !important
    .v-label
      margin-left: 30px !important
      top: 3px !important
    .v-label--active
      transform: translate(-30px, -16px) scale(0.75) !important
</style>
